import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    CircularProgress,
    Alert
} from '@mui/material';
import { Assessment as AssessmentIcon } from '@mui/icons-material';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { businessReportService } from '../../services/reportService';
import branchService from '../../services/branchService';
import ReportRunner from './reports/ReportRunner';
import GstReports from './reports/GstReports';
import ReportGuide from './reports/ReportGuide';

// The GST reports predate the report catalogue, so their notes live here
const GST_GUIDE = [
    { title: 'What it shows', points: [
        'Credit Booking GST Report: invoices raised to credit customers in the period, grouped by customer, with the GST charged on each.',
        'Instant Booking GST Report: paid and to-pay bookings in the period, grouped by the GSTIN of the party who pays.'
    ] },
    { title: 'How to read it', points: [
        'Total Amount is the value before GST. SGST and CGST are the two halves of the GST. Grand Total is the amount including GST.',
        'Only the GST is owed to the government; the value before GST is what the business keeps.',
        'The credit report uses the invoice date, so bookings not yet invoiced do not appear. Booking Register with Invoiced set to "Not invoiced" lists those.'
    ] },
    { title: 'What to do', points: [
        'Export each month for GST filing and match the totals with the invoices issued.'
    ] }
];

/**
 * One report, chosen from the sidebar's Reports section. The definition
 * (filters, columns) comes from the catalogue, which only lists the reports
 * this user may run.
 */
const Reports = () => {
    const { reportKey } = useParams();
    const [catalog, setCatalog] = useState(null);
    const [branches, setBranches] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const [reports, branchResponse] = await Promise.all([
                    businessReportService.getCatalog(),
                    branchService.getActiveBranches().catch(() => ({ data: [] }))
                ]);
                setCatalog(reports);
                setBranches(branchResponse?.data || []);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to load reports');
            }
        };
        load();
    }, []);

    const report = catalog?.find((r) => r.key === reportKey);
    const isGst = reportKey === 'gst';

    const renderContent = () => {
        if (isGst) {
            return (
                <>
                    <ReportGuide sections={GST_GUIDE} />
                    <GstReports />
                </>
            );
        }
        if (error) {
            return <Alert severity="error">{error}</Alert>;
        }
        if (!catalog) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (!report) {
            return <Alert severity="warning">This report does not exist or you do not have permission to view it.</Alert>;
        }
        return <ReportRunner report={report} branches={branches} />;
    };

    return (
        <DashboardLayout>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AssessmentIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                <Typography variant="h4" component="h1">
                    {isGst ? 'GST Reports' : 'Reports'}
                </Typography>
            </Box>
            {renderContent()}
        </DashboardLayout>
    );
};

export default Reports;
